import { useState } from 'react'
import { type PostContent } from '@backend/lib/platforms/instagram'
import { INSTAGRAM_RESTRICTIONS } from '@backend/lib/platforms/instagram/lib/restrictions'
import { InfoIcon } from '@/components/ui/icons/icons-new'
import { cn } from '@/lib/utils'
import { AiLabeledImage } from './ai-label-preview-overlay'
import type { AiLabelStyle } from '@backend/lib/platforms/aiDisclosure'

const IG_MIN_RATIO = INSTAGRAM_RESTRICTIONS.image.aspectRatio.min
const IG_MAX_RATIO = INSTAGRAM_RESTRICTIONS.image.aspectRatio.max

type Props = {
  postContent: PostContent
  account: { id: string; displayName: string; imageUrl?: string; subline?: string } | undefined
  _onStartAuth: () => void
  /** Draw the visible AI label that will be baked into the image at publish. */
  showAiLabel?: boolean
  /** Appearance of that label, so the preview matches the user's settings. */
  aiLabelStyle?: AiLabelStyle
}

export function WorkspacePreviewInstagram({
  account,
  postContent,
  _onStartAuth,
  showAiLabel = false,
  aiLabelStyle,
}: Props) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [prevPostContent, setPrevPostContent] = useState(postContent)
  const [willCrop, setWillCrop] = useState(false)
  const [imageContainerRatio, setImageContainerRatio] = useState(1)
  const [croppedCarouselIndices, setCroppedCarouselIndices] = useState<Set<number>>(new Set())

  if (prevPostContent !== postContent) {
    setPrevPostContent(postContent)
    setCurrentSlide(0)
    setWillCrop(false)
    setImageContainerRatio(1)
    setCroppedCarouselIndices(new Set())
  }

  return (
    <div
      className={cn(
        'mx-auto flex h-full w-full flex-col gap-3 rounded-lg text-black',
        postContent?.type.includes('Story') ? 'px-3' : 'p-3',
      )}>
      {/* Header */}
      {!postContent?.type.includes('Story') && (
        <div className="flex items-center justify-between">
          <ProfileHeader
            imageUrl={account?.imageUrl}
            displayName={account?.displayName || 'Your Name'}
            isStory={false}
          />
        </div>
      )}

      {/* Image */}
      <div className="-mx-3 flex items-center justify-center">
        {postContent?.type === 'image' ? (
          <div
            className="flex w-full items-center justify-center bg-gray-100"
            style={{ aspectRatio: imageContainerRatio }}>
            <AiLabeledImage
              src={postContent?.url}
              alt="Post"
              showAiLabel={showAiLabel}
              aiLabelStyle={aiLabelStyle}
              onLoad={(e) => {
                const ratio = e.currentTarget.naturalWidth / e.currentTarget.naturalHeight
                const clamped = Math.min(Math.max(ratio, IG_MIN_RATIO), IG_MAX_RATIO)
                setImageContainerRatio(clamped)
                if (ratio < IG_MIN_RATIO || ratio > IG_MAX_RATIO) {
                  setWillCrop(true)
                }
              }}
              // When Instagram will crop, the image fills the frame, so the
              // wrapper has to fill it too or the label drifts off the picture.
              wrapperClassName={willCrop ? 'h-full w-full' : undefined}
              className={
                willCrop ? 'h-full w-full object-cover' : 'max-h-full max-w-full object-contain'
              }
            />
          </div>
        ) : postContent?.type === 'reel' ? (
          <div className="flex aspect-square w-full items-center justify-center bg-gray-100">
            <video
              src={postContent?.url}
              className="max-h-full max-w-full object-contain"
              controls
              autoPlay
              muted
              playsInline
            />
          </div>
        ) : postContent?.type === 'carousel' ? (
          <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
            {/* Slides container */}
            <div
              className="flex h-full transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
              {postContent?.urls.map(({ type, url }, index) => (
                <div
                  key={index}
                  className="flex h-full w-full shrink-0 items-center justify-center">
                  {type === 'image' ? (
                    <AiLabeledImage
                      src={url}
                      crossOrigin="anonymous"
                      alt="Post"
                      showAiLabel={showAiLabel}
                      aiLabelStyle={aiLabelStyle}
                      onLoad={(e) => {
                        const ratio = e.currentTarget.naturalWidth / e.currentTarget.naturalHeight
                        if (ratio < IG_MIN_RATIO || ratio > IG_MAX_RATIO) {
                          setWillCrop(true)
                          setCroppedCarouselIndices((prev) => new Set(prev).add(index))
                        }
                      }}
                      wrapperClassName={
                        croppedCarouselIndices.has(index) ? 'h-full w-full' : undefined
                      }
                      className={
                        croppedCarouselIndices.has(index)
                          ? 'h-full w-full object-cover'
                          : 'max-h-full max-w-full object-contain'
                      }
                    />
                  ) : (
                    <video
                      src={url}
                      className="max-h-full max-w-full object-contain"
                      controls
                      autoPlay
                      muted
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Navigation dots */}
            {postContent?.urls.length > 1 && (
              <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 transform gap-1">
                {postContent?.urls.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 w-2 rounded-full transition-all ${
                      index === currentSlide ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Navigation arrows (optional - for better UX) */}
            {postContent?.urls.length > 1 && (
              <>
                {currentSlide > 0 && (
                  <button
                    onClick={() => setCurrentSlide(currentSlide - 1)}
                    className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 transform items-center justify-center rounded-full bg-black/20 text-white transition-all hover:bg-black/40">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                )}
                {currentSlide < postContent?.urls.length - 1 && (
                  <button
                    onClick={() => setCurrentSlide(currentSlide + 1)}
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 transform items-center justify-center rounded-full bg-black/20 text-white transition-all hover:bg-black/40">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                )}
              </>
            )}
          </div>
        ) : postContent?.type === 'imageStory' ? (
          <div className="aspect-9/16 relative flex max-h-[600px] items-center justify-center overflow-hidden rounded-md bg-black">
            {/* Stories bake the label regardless of level, because STORIES
                cannot carry `is_ai_generated` and Facebook stories drop
                captions — so a disclosed story has nothing else to carry it. */}
            <AiLabeledImage
              src={postContent?.url}
              className="max-h-full max-w-full object-contain"
              alt="Story"
              showAiLabel={showAiLabel}
              aiLabelStyle={aiLabelStyle}
            />
            <ProgressBar />
            <div className="absolute left-4 right-4 top-6">
              <ProfileHeader
                imageUrl={account?.imageUrl}
                displayName={account?.displayName || 'Your Name'}
                isStory={true}
              />
            </div>
          </div>
        ) : postContent?.type === 'videoStory' ? (
          <div className="aspect-9/16 relative flex max-h-[600px] items-center justify-center overflow-hidden rounded-md bg-black">
            <video
              src={postContent?.url}
              className="max-h-full max-w-full object-contain"
              controls
              autoPlay
              muted
              playsInline
            />
            <ProgressBar />
            <div className="absolute left-4 right-4 top-6">
              <ProfileHeader
                imageUrl={account?.imageUrl}
                displayName={account?.displayName || 'Your Name'}
                isStory={true}
              />
            </div>
          </div>
        ) : null}
      </div>

      {willCrop && !postContent?.type.includes('Story') && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 dark:border-amber-800 dark:bg-amber-950/30">
          <span className="shrink-0 rounded-full bg-amber-400 p-1">
            <InfoIcon width={12} height={12} color="black" />
          </span>
          <p className="text-xs text-amber-800 dark:text-amber-200">
            Image will be automatically cropped to fit Instagram&apos;s aspect ratio
          </p>
        </div>
      )}

      {!postContent?.type.includes('Story') && (
        <>
          <div className="space-y-3">
            {/* Action buttons */}
            <div className="flex items-center justify-between">
              <div className="flex gap-4">
                {/* Like */}
                <button className="text-gray-800">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                  </svg>
                </button>

                {/* Comment */}
                <button className="text-gray-800">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                  </svg>
                </button>

                {/* Share */}
                <button className="text-gray-800">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                  </svg>
                </button>
              </div>

              {/* Save */}
              <button className="text-gray-800">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"></path>
                </svg>
              </button>
            </div>

            {/* Likes and timestamp */}
            <div className="space-y-1">
              <p className="text-sm font-semibold">1,234 likes</p>
              <p className="text-xs text-gray-500">2h</p>
            </div>
          </div>

          {/* Caption */}
          {postContent?.caption && (
            <div className="">
              <p className="whitespace-pre-wrap text-xs">{postContent.caption}</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

const ProgressBar = () => {
  return (
    <div className="absolute left-2 right-2 top-2 flex gap-1">
      <div className="h-1 w-1/2 overflow-hidden rounded-full bg-white/30">
        <div className="h-full rounded-full bg-white"></div>
      </div>
      <div className="h-1 w-1/2 overflow-hidden rounded-full bg-white/30"></div>
    </div>
  )
}

const ProfileHeader = ({
  imageUrl,
  displayName,
  isStory,
}: {
  imageUrl: string | undefined
  displayName: string
  isStory: boolean
}) => {
  return (
    <div className="flex items-center gap-2">
      {/* Profile picture */}
      <div className="bg-linear-to-tr h-10 w-10 rounded-full from-yellow-400 to-purple-600 p-[2px]">
        <div className="bg-background flex h-full w-full items-center justify-center overflow-hidden rounded-full">
          {imageUrl ? (
            <img src={imageUrl} alt="Profile" className="h-full w-full object-cover" />
          ) : (
            <div className="bg-muted h-full w-full"></div>
          )}
        </div>
      </div>

      {/* Username and location */}
      <div className={cn('text-left', isStory && 'flex items-end gap-2 text-white')}>
        <span className={cn('text-[14px] font-semibold', !isStory && 'text-black')}>
          {displayName || 'Not authenticated'}
        </span>
        {isStory && <span className="text-[12px] text-white/70">17m</span>}
      </div>
    </div>
  )
}
